/**
 * POS offline support — an IndexedDB-backed item catalog cache and a durable
 * queue of sales made while the network was down.
 *
 * A retail till must keep ringing sales through a WAN blip. This module gives it
 * two things:
 *   1. A local copy of the item catalog, refreshed opportunistically while
 *      online, so barcode/name lookups still work offline.
 *   2. A persistent queue: a sale rung while offline is stored locally (with its
 *      idempotency key) and flushed to the server on reconnect. Because the key
 *      is stable, a flushed sale that the server already processed is de-duped
 *      rather than double-charged.
 *
 * This layer keeps an already-open till working through a network interruption.
 * Cold-start-while-offline (reloading the tab with no network) additionally
 * needs an app-shell service worker — tracked separately.
 *
 * No external dependency: raw IndexedDB, with graceful degradation (every call
 * resolves to a safe empty value if IndexedDB is unavailable).
 *
 * STORAGE IS NAMESPACED PER TENANT
 * --------------------------------
 * The database name embeds the JWT's `tenantDb` claim (see posSyncPolicy.js).
 * It used to be a single global 'journey_pos', which on a shared browser let one
 * tenant's operator read and flush another tenant's queued sales. Entries left
 * in that legacy database are rescued once, on first use, rather than stranded.
 *
 * A FAILED FLUSH NEVER SILENTLY DISCARDS A SALE
 * ---------------------------------------------
 * Queued sales are real money that has already changed hands. The rules for what
 * survives a failed sync attempt live in posSyncPolicy.classifySyncFailure and
 * are unit-tested; do not inline that decision here.
 */

import posSyncPolicy from './posSyncPolicy';

const {
  LEGACY_DB_NAME,
  MAX_SYNC_ATTEMPTS,
  classifySyncFailure,
  dbNameForTenant,
  tenantFromToken,
} = posSyncPolicy;

// v2 adds the catalog_meta store. onupgradeneeded creates only what is missing,
// so an existing database gains the new store without touching queued sales.
const DB_VERSION = 2;
const STORE_CATALOG = 'catalog';
const STORE_QUEUE = 'sale_queue';
const STORE_META = 'catalog_meta';
const META_KEY = 'catalog';

const LEGACY_MIGRATED_FLAG = 'pos.offline.legacyMigrated';

// One open-handle promise per database name. A single shared promise was fine
// when there was one database; with a database per tenant, switching tenants
// inside one page load must not reuse the previous tenant's handle.
const _dbPromises = new Map();

const storedToken = () => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  } catch {
    return null;
  }
};

/** Current tenant's database name, derived from the stored JWT. */
const currentDbName = () => dbNameForTenant(tenantFromToken(storedToken()));

const createStores = (db) => {
  if (!db.objectStoreNames.contains(STORE_CATALOG)) {
    db.createObjectStore(STORE_CATALOG, { keyPath: 'id' });
  }
  if (!db.objectStoreNames.contains(STORE_QUEUE)) {
    db.createObjectStore(STORE_QUEUE, { keyPath: 'key' });
  }
  if (!db.objectStoreNames.contains(STORE_META)) {
    db.createObjectStore(STORE_META, { keyPath: 'id' });
  }
};

const openNamed = (name) => {
  if (_dbPromises.has(name)) return _dbPromises.get(name);
  const promise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    let req;
    try {
      req = indexedDB.open(name, DB_VERSION);
    } catch {
      return resolve(null);
    }
    req.onupgradeneeded = () => createStores(req.result);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  _dbPromises.set(name, promise);
  return promise;
};

const openDB = async () => {
  await rescueLegacyQueue();
  return openNamed(currentDbName());
};

const runTx = (db, store, mode, fn) => {
  if (!db) return Promise.resolve(null);
  return new Promise((resolve) => {
    let result;
    let t;
    try {
      t = db.transaction(store, mode);
    } catch {
      resolve(null);
      return;
    }
    const os = t.objectStore(store);
    try {
      result = fn(os);
    } catch {
      resolve(null);
      return;
    }
    t.oncomplete = () => resolve(result !== undefined ? result : true);
    t.onerror = () => resolve(null);
    t.onabort = () => resolve(null);
  });
};

const tx = async (store, mode, fn) => runTx(await openDB(), store, mode, fn);

const getAll = (os) =>
  new Promise((resolve) => {
    const r = os.getAll();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror = () => resolve([]);
  });

// ─── one-shot rescue of the pre-namespacing database ───────────────────────
/**
 * Move any sales queued under the old global 'journey_pos' database into the
 * current tenant's namespaced database.
 *
 * Deliberately opened WITHOUT a version so an absent legacy database is
 * detected rather than created: if onupgradeneeded fires, this browser never
 * had one, and the empty database we just made is deleted again.
 *
 * The legacy rows carry no tenant marker — there was nowhere to put one — so
 * they are attributed to whichever tenant is signed in when the rescue runs.
 * On a dedicated till (the real deployment) that is always the right tenant. On
 * a shared browser it could mis-attribute, which is why each rescued entry is
 * stamped `migratedFromLegacy` and must be reviewed rather than trusted blindly.
 * Losing a paid-for sale is strictly worse than importing one that needs a look.
 */
let _rescuePromise = null;
const rescueLegacyQueue = () => {
  if (_rescuePromise) return _rescuePromise;
  _rescuePromise = (async () => {
    if (typeof indexedDB === 'undefined') return;
    try {
      if (localStorage.getItem(LEGACY_MIGRATED_FLAG) === '1') return;
    } catch {
      return;
    }
    // Do nothing until a tenant is known. Rescuing while signed out would file
    // the salvaged sales under the 'default' namespace, where the tenant that
    // actually rang them would never see them again. Leaving the flag unset
    // means this simply runs again after login (resetHandles clears the memo).
    if (!tenantFromToken(storedToken())) return;

    const legacy = await new Promise((resolve) => {
      let req;
      let freshlyCreated = false;
      try {
        req = indexedDB.open(LEGACY_DB_NAME);
      } catch {
        return resolve(null);
      }
      req.onupgradeneeded = () => { freshlyCreated = true; };
      req.onsuccess = () => {
        const db = req.result;
        if (freshlyCreated || !db.objectStoreNames.contains(STORE_QUEUE)) {
          db.close();
          try { indexedDB.deleteDatabase(LEGACY_DB_NAME); } catch { /* best effort */ }
          return resolve(null);
        }
        resolve(db);
      };
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    });

    if (!legacy) {
      try { localStorage.setItem(LEGACY_MIGRATED_FLAG, '1'); } catch { /* ignore */ }
      return;
    }

    // runTx resolves with whatever the callback returned — here, getAll's own
    // promise — so it needs unwrapping twice before it is an array.
    const pending = (await (await runTx(legacy, STORE_QUEUE, 'readonly', (os) => getAll(os)))) || [];

    if (pending.length) {
      const target = await openNamed(currentDbName());
      const copied = await runTx(target, STORE_QUEUE, 'readwrite', (os) => {
        for (const entry of pending) {
          if (entry && entry.key) os.put({ ...entry, migratedFromLegacy: true });
        }
        return true;
      });
      // Nothing was copied — leave the legacy database untouched so the sales
      // are still there to rescue on the next attempt.
      if (!copied) {
        legacy.close();
        return;
      }
      console.warn(
        `[POS offline] Rescued ${pending.length} queued sale(s) from the pre-namespacing local store. Review them after sync.`
      );
    }

    legacy.close();
    try { indexedDB.deleteDatabase(LEGACY_DB_NAME); } catch { /* best effort */ }
    try { localStorage.setItem(LEGACY_MIGRATED_FLAG, '1'); } catch { /* ignore */ }
  })();
  return _rescuePromise;
};

export const isSupported = () => typeof indexedDB !== 'undefined';

/**
 * Drop every cached handle so the next call re-resolves the tenant from the
 * current token. Call on login and logout, alongside clearReferenceCache().
 *
 * Queued sales are intentionally NOT deleted: an unsynced sale must outlive the
 * session that rang it, and it is namespaced by tenant so it stays invisible to
 * the next operator unless they sign into the same tenant.
 */
export const resetHandles = () => {
  for (const promise of _dbPromises.values()) {
    Promise.resolve(promise).then((db) => { try { db && db.close(); } catch { /* ignore */ } });
  }
  _dbPromises.clear();
  // Clear the one-shot legacy-rescue memo too: a rescue skipped because nobody
  // was signed in must get another chance now that the tenant is known.
  _rescuePromise = null;
};

// ─── catalog cache ─────────────────────────────────────────────────────────
/** Upsert a batch of items into the local catalog (called after online lookups). */
export const cacheItems = async (items) => {
  if (!Array.isArray(items) || !items.length) return;
  await tx(STORE_CATALOG, 'readwrite', (os) => {
    for (const it of items) {
      if (it && it.id != null) os.put(it);
    }
  });
};

/** Full local catalog (for warming or diagnostics). */
export const getCachedItems = async () => {
  const all = await tx(STORE_CATALOG, 'readonly', (os) => getAll(os));
  return (await all) || [];
};

export const catalogCount = async () => (await getCachedItems()).length;

/**
 * Search the local catalog the same way the server lookup does: exact barcode
 * first, else a case-insensitive contains match on code/name.
 */
export const searchCachedItems = async ({ barcode, q, limit = 30 } = {}) => {
  const items = await getCachedItems();
  const bc = String(barcode || '').trim();
  if (bc) {
    return items.filter((it) => String(it.barcode || '').trim() === bc).slice(0, limit);
  }
  const term = String(q || '').trim().toLowerCase();
  if (!term) return items.slice(0, limit);
  return items
    .filter(
      (it) =>
        String(it.item_name || '').toLowerCase().includes(term) ||
        String(it.item_code || '').toLowerCase().includes(term) ||
        String(it.barcode || '').toLowerCase().includes(term)
    )
    .slice(0, limit);
};

// ─── catalog sync ──────────────────────────────────────────────────────────
/** Sync bookkeeping: when we last synced and how much we hold. */
export const getCatalogMeta = async () => {
  const meta = await tx(STORE_META, 'readonly', (os) =>
    new Promise((resolve) => {
      const r = os.get(META_KEY);
      r.onsuccess = () => resolve(r.result || null);
      r.onerror = () => resolve(null);
    })
  );
  return (await meta) || null;
};

const setCatalogMeta = async (patch) => {
  const current = (await getCatalogMeta()) || {};
  await tx(STORE_META, 'readwrite', (os) => {
    os.put({ ...current, ...patch, id: META_KEY });
    return true;
  });
};

// A runaway cursor must not spin forever against a moving table.
const MAX_SYNC_PAGES = 250;
// Above this the local copy stops being a sensible browser-side cache.
const CATALOG_SIZE_WARN = 100000;

/**
 * Pull the item catalog into IndexedDB so the till can sell while offline.
 *
 * @param {(params:object)=>Promise<{data:object}>} fetcher usually posService.syncCatalog
 * @param {{ full?: boolean, warehouseId?: number|string }} options
 *   full — ignore the stored watermark and re-read everything. Use this when a
 *   shift is opened: it is the only path that reliably picks up price-list
 *   edits (see getCatalogForSync in posModel.js) and the only one that prunes
 *   items which have since been deactivated.
 *
 * Failures resolve rather than throw: a sync that cannot run must never break
 * the till, it just leaves the previous cache in place.
 */
export const syncCatalog = async (fetcher, { full = false, warehouseId = null } = {}) => {
  if (!isSupported()) return { ok: false, synced: 0, reason: 'unsupported' };

  const meta = await getCatalogMeta();
  const since = full ? null : meta?.lastSyncAt || null;

  let afterId = 0;
  let pages = 0;
  let synced = 0;
  let serverTime = null;
  const seenIds = full ? new Set() : null;

  try {
    for (;;) {
      const res = await fetcher({
        ...(since ? { updated_since: since } : {}),
        ...(warehouseId ? { warehouse_id: warehouseId } : {}),
        after_id: afterId,
      });
      const data = res?.data || {};
      const items = Array.isArray(data.items) ? data.items : [];

      if (items.length) {
        await cacheItems(items);
        synced += items.length;
        if (seenIds) items.forEach((it) => seenIds.add(it.id));
      }
      serverTime = data.server_time || serverTime;
      pages += 1;

      if (!data.next_after_id) break;
      afterId = data.next_after_id;

      if (pages >= MAX_SYNC_PAGES) {
        console.warn(
          `[POS offline] Catalog sync stopped at ${MAX_SYNC_PAGES} pages (${synced} items). The local cache is incomplete.`
        );
        // Deliberately not pruning: an incomplete sweep would delete items it
        // simply never reached.
        return { ok: false, synced, pages, truncated: true };
      }
    }

    // A full sweep is authoritative, so anything it did not return is no longer
    // sellable — deactivated, deleted, or moved out of this warehouse. Without
    // this, a withdrawn item stays ringable offline forever.
    if (seenIds) {
      const cached = await getCachedItems();
      const stale = cached.filter((it) => it && !seenIds.has(it.id)).map((it) => it.id);
      if (stale.length) {
        await tx(STORE_CATALOG, 'readwrite', (os) => {
          stale.forEach((id) => os.delete(id));
          return true;
        });
      }
    }

    const itemCount = await catalogCount();
    if (itemCount > CATALOG_SIZE_WARN) {
      console.warn(
        `[POS offline] Local catalog holds ${itemCount} items — above the ${CATALOG_SIZE_WARN} the browser cache is sized for.`
      );
    }

    await setCatalogMeta({
      // The server's clock, never the till's: the delta window must not depend
      // on a device whose time may be wrong.
      lastSyncAt: serverTime || meta?.lastSyncAt || null,
      ...(full ? { lastFullSyncAt: serverTime } : {}),
      itemCount,
    });

    return { ok: true, synced, pages, full, itemCount };
  } catch (err) {
    // Offline, or the endpoint is unavailable. Keep whatever is already cached.
    return { ok: false, synced, pages, reason: err?.message || 'sync failed' };
  }
};

// ─── offline sale queue ────────────────────────────────────────────────────
/**
 * Enqueue a sale made offline. The payload MUST carry an idempotency_key so the
 * later flush cannot double-post.
 * @returns {Promise<object|null>} the stored queue entry.
 */
export const enqueueSale = async (payload, meta = {}) => {
  const key = payload?.idempotency_key;
  if (!key) throw new Error('Cannot queue a sale without an idempotency_key.');
  const entry = {
    key,
    payload,
    meta,
    attempts: 0,
    needsAttention: false,
    lastError: null,
    queuedAt: new Date().toISOString(),
  };
  const ok = await tx(STORE_QUEUE, 'readwrite', (os) => { os.put(entry); return true; });
  return ok ? entry : null;
};

export const getQueuedSales = async () => {
  const all = await tx(STORE_QUEUE, 'readonly', (os) => getAll(os));
  return (await all) || [];
};

export const queuedCount = async () => (await getQueuedSales()).length;

/** Queued sales that have exhausted their retries and need a human. */
export const getNeedsAttentionSales = async () =>
  (await getQueuedSales()).filter((e) => e && e.needsAttention);

export const needsAttentionCount = async () => (await getNeedsAttentionSales()).length;

export const removeQueuedSale = async (key) => {
  await tx(STORE_QUEUE, 'readwrite', (os) => { os.delete(key); return true; });
};

const updateQueuedSale = async (entry) => {
  await tx(STORE_QUEUE, 'readwrite', (os) => { os.put(entry); return true; });
};

/** Sales the server refused on sync, kept until a person resolves them. */
export const getRejectedSales = async () =>
  (await getQueuedSales()).filter((e) => e && e.rejected);

/**
 * Put a rejected sale back in the queue, optionally re-pointed at the till's
 * current open session (the usual cause of a rejection is that the session it
 * was rung in has since been closed). Same idempotency key, so it cannot post
 * twice.
 */
export const requeueRejectedSale = async (key, { sessionId } = {}) => {
  const entry = (await getQueuedSales()).find((e) => e && e.key === key);
  if (!entry) return null;
  const payload = sessionId ? { ...entry.payload, session_id: sessionId } : entry.payload;
  const updated = { ...entry, payload, rejected: false, needsAttention: false, attempts: 0 };
  await updateQueuedSale(updated);
  return updated;
};

/**
 * Flush every queued sale through `poster` (typically posService.createSale).
 *
 * An entry is removed ONLY when the server accepts it, or when the server
 * evaluates it and rejects it on its merits. Everything else — no response at
 * all, an expired token, a rate limit, a 5xx — leaves the sale queued, because
 * the money has already been taken and the attempt failing says nothing about
 * whether the sale is valid.
 *
 * @returns {Promise<{ synced:number, failed:Array, attention:Array, remaining:number }>}
 */
export const flushQueue = async (poster) => {
  // Rejected sales wait for a person (see getRejectedSales); retrying them
  // automatically would only be refused again for the same reason.
  const entries = (await getQueuedSales()).filter((e) => e && !e.rejected);
  let synced = 0;
  const failed = [];
  const attention = [];

  for (const entry of entries) {
    try {
      await poster(entry.payload);
      await removeQueuedSale(entry.key);
      synced += 1;
    } catch (err) {
      const attempts = Number(entry.attempts || 0) + 1;
      const verdict = classifySyncFailure(err, attempts);
      const reason = err?.response?.data?.message || err?.message || 'Sync failed';

      if (verdict === 'drop') {
        // The server looked at this sale and refused it (closed session,
        // stock, a total mismatch). The customer has already paid and left,
        // so the sale is KEPT, marked rejected, until someone retries it into
        // an open session or deliberately removes it. Deleting it here lost
        // the only record of money already in the drawer.
        await updateQueuedSale({
          ...entry,
          attempts,
          rejected: true,
          needsAttention: true,
          lastError: reason,
          lastAttemptAt: new Date().toISOString(),
        });
        failed.push({ key: entry.key, meta: entry.meta, reason });
        continue;
      }

      const updated = {
        ...entry,
        attempts,
        needsAttention: verdict === 'needs_attention',
        lastError: reason,
        lastAttemptAt: new Date().toISOString(),
      };
      await updateQueuedSale(updated);

      if (verdict === 'needs_attention') {
        attention.push({ key: entry.key, meta: entry.meta, attempts, reason });
      }
    }
  }

  return { synced, failed, attention, remaining: await queuedCount() };
};

const posOffline = {
  isSupported,
  resetHandles,
  cacheItems,
  getCachedItems,
  catalogCount,
  searchCachedItems,
  getCatalogMeta,
  syncCatalog,
  enqueueSale,
  getQueuedSales,
  queuedCount,
  getNeedsAttentionSales,
  needsAttentionCount,
  getRejectedSales,
  requeueRejectedSale,
  removeQueuedSale,
  flushQueue,
  MAX_SYNC_ATTEMPTS,
};

export default posOffline;
